import React from 'react';
import { Button } from '@/components/UI';
import DataTable from '@/components/UI/DataTable';
import { FaInfoCircle } from 'react-icons/fa';
import { getBucketIdentityKey, getGroupDisplayLabel, toSelector } from './helpers';
import { buildParameterGroupColumns } from './parameterGroupColumns';
import type { useScorecardRuleStatus } from '@/hooks/useScorecardRuleStatus';
import type { CanonicalParameterFeatureIdentity, ParameterDiscoveryMetric, ParameterGroup, ParameterTableRow } from './types';

type ScorecardRuleStatusApi = ReturnType<typeof useScorecardRuleStatus>;

export interface ParameterDetailsContentProps {
    bulkAddingPath: string | null;
    customBucketsByIdentityKey: Record<string, ParameterGroup[]>;
    getParameterDisplayLabel: (parameter: {
        path: string;
        canonicalFeature?: CanonicalParameterFeatureIdentity | null | undefined;
    }) => string;
    isCanonicalValueInScorecard: ScorecardRuleStatusApi['isCanonicalValueInScorecard'];
    minEntriesThreshold: number;
    onOpenAddModal: (parameterRow: ParameterTableRow, label: string, group: ParameterGroup) => void;
    onQuickAddAllClick: (parameterRow: ParameterTableRow) => void;
    onQuickAddRule: (parameterRow: ParameterTableRow, label: string, group: ParameterGroup) => Promise<void>;
    optimizationMode: ParameterDiscoveryMetric;
    parameterRow: ParameterTableRow;
    selectedScorecardIdTop: number | null;
    setShowAdvancedValueMetrics: React.Dispatch<React.SetStateAction<boolean>>;
    showAdvancedValueMetrics: boolean;
}

// Detailed per-value breakdown for expanded rows
const ParameterDetailsContent = (props: ParameterDetailsContentProps) => {
    const {
        bulkAddingPath,
        customBucketsByIdentityKey,
        getParameterDisplayLabel,
        isCanonicalValueInScorecard,
        minEntriesThreshold,
        onOpenAddModal,
        onQuickAddAllClick,
        onQuickAddRule,
        optimizationMode,
        parameterRow,
        selectedScorecardIdTop,
        setShowAdvancedValueMetrics,
        showAdvancedValueMetrics
    } = props;

    const rowPath = parameterRow.path;
    const rowBucketKey = getBucketIdentityKey(parameterRow);
    const primaryIdentityLabel = getParameterDisplayLabel(parameterRow);

    // Select correct groups and baseline based on optimization mode
    const stockGroups =
        optimizationMode === 'stock' && customBucketsByIdentityKey[rowBucketKey]
            ? customBucketsByIdentityKey[rowBucketKey]
            : parameterRow.groups;

    const rawDisplayGroups = optimizationMode === 'options' && parameterRow.optionGroups ? parameterRow.optionGroups : stockGroups;
    const displayGroups = rawDisplayGroups.filter((group) => group.count >= minEntriesThreshold);
    const hiddenByMinEntriesCount = rawDisplayGroups.length - displayGroups.length;

    const displayBaseline =
        optimizationMode === 'options' && parameterRow.optionBaseline !== undefined ? parameterRow.optionBaseline : parameterRow.baseline;

    const { adaptive } = parameterRow;

    const columnsToUse = buildParameterGroupColumns({
        displayBaseline,
        displayGroups,
        isCanonicalValueInScorecard,
        parameterRow,
        selectedScorecardIdTop,
        showAdvancedValueMetrics,
        onOpenAddModal,
        onQuickAddRule
    });

    // Check if we're in options mode but have no option data
    if (optimizationMode === 'options' && (!parameterRow.hasOptionData || !parameterRow.optionGroups)) {
        return (
            <div className="p-4">
                <div className="flex items-center gap-2 text-amber-400">
                    <FaInfoCircle />
                    <span>No option data available for this parameter</span>
                </div>
                {parameterRow.optionDataCoverage !== undefined && parameterRow.optionDataCoverage < 1 && (
                    <p className="mt-2 text-sm text-zinc-400">
                        Only {(parameterRow.optionDataCoverage * 100).toFixed(0)}% of entries have option data
                    </p>
                )}
            </div>
        );
    }

    if (rawDisplayGroups.length > 0 && displayGroups.length === 0) {
        return (
            <div className="p-4">
                <div className="flex items-center gap-2 text-amber-400">
                    <FaInfoCircle />
                    <span>No values meet the current Min Entries filter</span>
                </div>
                <p className="mt-2 text-sm text-zinc-400">
                    {rawDisplayGroups.length} value{rawDisplayGroups.length === 1 ? '' : 's'} exist for this parameter, but all are below
                    the current threshold of {minEntriesThreshold} entries.
                </p>
            </div>
        );
    }

    return (
        <div className="p-4">
            {adaptive && (
                <div className="mb-4 rounded bg-zinc-800 p-3">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-zinc-400">Adaptive Assessment</p>
                            <p className="text-white font-medium">
                                {adaptive.shape === 'monotonic'
                                    ? `Monotonic (${adaptive.shapeDiagnostics?.direction === 'smaller' ? 'smaller is better' : 'larger is better'})`
                                    : adaptive.shape === 'band'
                                      ? 'Sweet Spot Band'
                                      : 'Ambiguous'}
                            </p>
                        </div>
                        <div className="text-right text-zinc-400 text-sm">
                            {adaptive.roi?.type === 'threshold' && (
                                <span>
                                    Threshold: {adaptive.roi.threshold?.toFixed(2)} • Coverage: {(adaptive.roi.coverage * 100).toFixed(1)}%
                                    • Weighted LCB: {adaptive.roi.ciLowerAgg.toFixed(2)}pp{' '}
                                    {adaptive.roi.confidence ? `• ${adaptive.roi.confidence} confidence` : ''}
                                </span>
                            )}
                            {adaptive.roi?.type === 'band' && adaptive.roi.band && (
                                <span>
                                    Band: {adaptive.roi.band.a.toFixed(2)} to {adaptive.roi.band.b.toFixed(2)} • Coverage:{' '}
                                    {(adaptive.roi.coverage * 100).toFixed(1)}% • Weighted LCB: {adaptive.roi.ciLowerAgg.toFixed(2)}pp{' '}
                                    {adaptive.roi.confidence ? `• ${adaptive.roi.confidence} confidence` : ''}
                                </span>
                            )}
                            {!adaptive.roi && (
                                <span>No robust ROI detected{adaptive.rejectionReason ? ` (${adaptive.rejectionReason})` : ''}</span>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {optimizationMode === 'options' && parameterRow.optionDataCoverage !== undefined && parameterRow.optionDataCoverage < 0.5 && (
                <div className="mb-3 p-2 bg-amber-900/20 border border-amber-600/50 rounded">
                    <span className="text-amber-400 text-sm">
                        ⚠️ Limited option data: Only {(parameterRow.optionDataCoverage * 100).toFixed(0)}% of entries have option data
                    </span>
                </div>
            )}
            <div className="mb-3">
                <span className="text-zinc-400 text-sm">
                    {optimizationMode === 'options' ? 'Option' : 'Stock'} performance by value for
                    <strong className="text-zinc-200"> {primaryIdentityLabel}</strong>
                    {parameterRow.canonicalFeature ? <span className="text-zinc-500"> ({parameterRow.path})</span> : null}
                    {' • '}Baseline: {displayBaseline.toFixed(2)}%
                </span>
            </div>
            <div className="mb-2 flex items-center justify-end gap-2">
                {(() => {
                    if (!selectedScorecardIdTop) return null;

                    if (!parameterRow.scorecardCandidate) {
                        return (
                            <Button
                                disabled
                                size="sm"
                                title="This parameter is not scorecard-authorable from the registry"
                                variant="secondary"
                            >
                                Quick Add All
                            </Button>
                        );
                    }

                    const groupsForCount = displayGroups.filter((g) => g.hasData !== false);
                    let addable = 0;

                    for (const g of groupsForCount) {
                        const label = getGroupDisplayLabel(g);
                        const selector = toSelector(label, g.value);

                        const exists = isCanonicalValueInScorecard({
                            serializedContextFeatureRef: parameterRow.scorecardCandidate.serializedContextFeatureRef,
                            selectorType: selector.type,
                            selector
                        });

                        if (!exists) addable += 1;
                    }

                    return (
                        <Button
                            disabled={!selectedScorecardIdTop || addable === 0 || bulkAddingPath === rowPath}
                            size="sm"
                            title={
                                !selectedScorecardIdTop
                                    ? 'Select a scorecard first'
                                    : addable === 0
                                      ? 'No new values to add'
                                      : 'Add all visible values to the selected scorecard'
                            }
                            variant="primary"
                            onClick={() => onQuickAddAllClick(parameterRow)}
                        >
                            {bulkAddingPath === rowPath ? 'Adding…' : `Quick Add All${addable > 0 ? ` (${addable})` : ''}`}
                        </Button>
                    );
                })()}
                <label className="text-xs text-zinc-300 flex items-center gap-2">
                    <input
                        checked={showAdvancedValueMetrics}
                        className="cursor-pointer"
                        type="checkbox"
                        onChange={(e) => setShowAdvancedValueMetrics(e.target.checked)}
                    />
                    Show advanced metrics
                </label>
            </div>
            <DataTable
                allowOverflow
                shrinkToContent
                sort
                striped
                columns={columnsToUse}
                data={displayGroups}
                pagination={{ enabled: displayGroups.length > 10, pageSize: 10 }}
                search={false}
            />
            {hiddenByMinEntriesCount > 0 && (
                <p className="mt-2 text-xs text-zinc-500">
                    {hiddenByMinEntriesCount} lower-count value{hiddenByMinEntriesCount === 1 ? '' : 's'} hidden by Min Entries.
                </p>
            )}
        </div>
    );
};

export default ParameterDetailsContent;
